import React, { useState } from 'react';
import { toast } from 'sonner';
import PropTypes from 'prop-types';
import jwtEncode from 'jwt-encode';
import { jwtDecode } from 'jwt-decode';
import { getUserName, formatDate } from 'helpers';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAPI, useGetPilgrim, useGetUser } from 'hooks';
import Locales from 'locales';
import { Switch } from 'components/ui/switch';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { ChevronDown } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import MedicalVisitHistory from './MedicalVisitHistory';
import { Textarea } from 'components/ui/textarea';

const MedicalCheckupModal = ({ isOpened = false, pilgrimData = null, closeModal = () => {} }) => {
  const {
    id,
    firstname,
    lastname,
    npi,
    note,
    passport,
    birthdate,
    health,
    categoryId,
    isPassportValid,
    passportExpirationDate,
    isInternational,
    isApprovedForMSStep,
  } = useGetPilgrim(pilgrimData);

  const { user, isMSSecondary, isMSPrimary } = useGetUser();
  const { api } = useAPI();
  const queryClient = useQueryClient();
  const isMutating = queryClient.isMutating() > 0;

  const disabledForMSSecondary = isMSSecondary && isApprovedForMSStep !== null;

  const [q1IsVaxCovid, setQ1IsVaxCovid] = useState(health.q1_is_vax_covid || false);
  const [q2IsVaxMeningitis, setQ2IsVaxMeningitis] = useState(health.q2_is_vax_meningitis || false);
  const [q3IsVaxYellow, setQ3IsVaxYellow] = useState(health.q3_is_vax_yellow || false);
  const [q4IsVaxPoliomyelitis, setQ4IsVaxPoliomyelitis] = useState(health.q4_is_vax_poliomyelitis || false);
  const [q6IsVaxFlu, setQ6IsVaxFlu] = useState(health.q6_is_vax_flu || false);
  const [q7aIsIssuePsychomotor, setQ7aIsIssuePsychomotor] = useState(health.q7a_is_issue_psychomotor || false);
  const [q7bIsIssueLocomotor, setQ7bIsIssueLocomotor] = useState(health.q7b_is_issue_locomotor || false);
  const [q7cIsIssueSeeing, setQ7cIsIssueSeeing] = useState(health.q7c_is_issue_seeing || false);
  const [q7dIsIssueChronic, setQ7dIsIssueChronic] = useState(health.q7d_is_issue_chronic || false);
  const [healthNote, setHealthNote] = useState('');
  const [isHistoryOpened, setIsHistoryOpened] = useState(false);

  const pilgrim = {
    first_name: firstname,
    last_name: lastname,
    npi,
    passport,
    birthdate: formatDate(birthdate, 'yyyy-MM-dd'),
    is_passport_valid: isPassportValid,
    passport_expiration: passportExpirationDate ? formatDate(passportExpirationDate, 'yyyy-MM-dd') : null,
    is_international: isInternational,
  };

  const healthData = {
    q1_is_vax_covid: q1IsVaxCovid,
    q2_is_vax_meningitis: q2IsVaxMeningitis,
    q3_is_vax_yellow: q3IsVaxYellow,
    q4_is_vax_poliomyelitis: q4IsVaxPoliomyelitis,
    q6_is_vax_flu: q6IsVaxFlu,
    q7a_is_issue_psychomotor: q7aIsIssuePsychomotor,
    q7b_is_issue_locomotor: q7bIsIssueLocomotor,
    q7c_is_issue_seeing: q7cIsIssueSeeing,
    q7d_is_issue_chronic: q7dIsIssueChronic,
    q7e_is_chronic_treatment: false, // This field is not represented in the form, defaulting to false
  };

  const checkHealthStatus = (data) =>
    data.q1_is_vax_covid &&
    data.q2_is_vax_meningitis &&
    data.q3_is_vax_yellow &&
    data.q4_is_vax_poliomyelitis &&
    data.q6_is_vax_flu &&
    !data.q7a_is_issue_psychomotor &&
    !data.q7b_is_issue_locomotor &&
    !data.q7c_is_issue_seeing &&
    !data.q7d_is_issue_chronic &&
    !data.q7e_is_chronic_treatment;

  let previousLogs = [];
  if (health.notes) {
    const decodedNotes = jwtDecode(health.notes);
    previousLogs = Array.isArray(decodedNotes) ? decodedNotes : [decodedNotes];
  }

  // Retroactive log
  if (isApprovedForMSStep !== null && (!health.notes || health.notes.trim() === '') && previousLogs.length === 0) {
    const initialLog = {
      pilgrim,
      health: { ...health },
      doctorEmail: user.email,
      doctorName: getUserName(user.first_name, user.last_name),
      checkupDate: null,
      checkupStatus: checkHealthStatus(health) ? 'Apte' : 'Inapte',
      userNote: note,
    };
    previousLogs.push(initialLog);
  }

  const { mutate: medicalCheckup } = useMutation({
    mutationFn: (data) => api.update('trip', data, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trip/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/stats/default/'] });
      queryClient.invalidateQueries({ queryKey: ['agency/'] });
      queryClient.invalidateQueries({ queryKey: ['agency/', 'is_sa'] });
      toast.success(
        Locales.formatString(Locales.trip.toast.pilgrim_updated, {
          fullname: getUserName(firstname, lastname),
        })
      );
      closeModal();
    },
  });

  const handleMedicalCheckup = () => {
    const healthLog = {
      pilgrim,
      health: { ...healthData },
      doctorEmail: user.email,
      doctorName: getUserName(user.first_name, user.last_name),
      checkupDate: formatDate(new Date(), "EEEE d MMMM yyyy 'à' HH:mm:ss"),
      checkupStatus: checkHealthStatus(healthData) ? 'Apte' : 'Inapte',
      userNote: healthNote,
    };

    const updatedLogs = [...previousLogs, healthLog];
    const updatedToken = jwtEncode(updatedLogs, process.env.REACT_APP_JWT_SECRET);

    const updatedPilgrim = {
      pilgrim,
      health: { ...healthData, notes: updatedToken },
      note,
      category_id: categoryId,
    };

    medicalCheckup(updatedPilgrim);
  };

  return (
    <Dialog open={isOpened} onOpenChange={closeModal}>
      <DialogContent className="grid max-h-[calc(100dvh-48px)] w-[640px] max-w-[calc(100%-32px)] grid-rows-[auto_minmax(0,1fr)_auto] gap-0 overflow-hidden rounded-[11px] border-[#e0e6e2] bg-white p-0 shadow-[0_16px_70px_rgba(20,42,37,0.2)] max-sm:max-h-[calc(100dvh-54px)] max-sm:max-w-full max-sm:rounded-b-none">
        <DialogHeader className="shrink-0 space-y-0 border-b border-[#e3e8e7] px-6 pb-[19px] pt-[21px] pr-[66px] text-left max-sm:px-[18px] max-sm:pb-4 max-sm:pt-[18px] max-sm:pr-[54px]">
          <DialogTitle className="text-[19px] font-semibold leading-[1.4] tracking-[-0.35px] max-sm:text-lg">
            {Locales.trip.modal.title.certificate}
          </DialogTitle>
          <DialogDescription className="mt-[5px] break-words text-xs leading-[1.6] text-[#63716a]">
            {getUserName(firstname, lastname)} · NPI {npi}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 overflow-y-auto overscroll-contain px-6 pb-6 pt-[22px] [scrollbar-color:#c8d0cb_transparent] [scrollbar-width:thin] max-sm:px-[18px] max-sm:pb-[22px] max-sm:pt-[19px]">
          <section aria-labelledby="vaccination-title">
            <h2 id="vaccination-title" className="mb-1.5 text-sm font-semibold leading-[1.5]">
              {Locales.trip.medical_certificate.vaccination}
            </h2>
            {[
              {
                id: 'covid',
                label: Locales.trip.medical_certificate.coronavirus,
                desc: Locales.trip.medical_certificate.covid19_vaccine,
                state: q1IsVaxCovid,
                setState: setQ1IsVaxCovid,
              },
              {
                id: 'meningitis',
                label: Locales.trip.medical_certificate.meningitis,
                desc: Locales.trip.medical_certificate.meningitis_vaccine,
                state: q2IsVaxMeningitis,
                setState: setQ2IsVaxMeningitis,
              },
              {
                id: 'yellow-fever',
                label: Locales.trip.medical_certificate.yellow_fever,
                desc: Locales.trip.medical_certificate.yellow_fever_vaccine,
                state: q3IsVaxYellow,
                setState: setQ3IsVaxYellow,
              },
              {
                id: 'polio',
                label: Locales.trip.medical_certificate.polio,
                desc: Locales.trip.medical_certificate.polio_vaccine,
                state: q4IsVaxPoliomyelitis,
                setState: setQ4IsVaxPoliomyelitis,
              },
              {
                id: 'flu',
                label: Locales.trip.medical_certificate.flu,
                desc: Locales.trip.medical_certificate.flu_vaccine,
                state: q6IsVaxFlu,
                setState: setQ6IsVaxFlu,
              },
            ].map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-5 border-b border-[#edf0ee] py-3 last:border-b-0 last:pb-0 max-sm:gap-3.5"
              >
                <Label
                  htmlFor={'medical-' + item.id}
                  className={
                    disabledForMSSecondary
                      ? 'min-w-0 flex-1 cursor-not-allowed opacity-60'
                      : 'min-w-0 flex-1 cursor-pointer'
                  }
                >
                  <span className="block text-[13px] font-medium leading-[1.5]">{item.label}</span>
                  <span className="mt-[3px] block text-xs font-normal leading-[1.6] text-[#66736d]">{item.desc}</span>
                </Label>
                <Switch
                  id={'medical-' + item.id}
                  checked={item.state}
                  onCheckedChange={item.setState}
                  disabled={disabledForMSSecondary}
                />
              </div>
            ))}
          </section>

          <section className="mt-[23px]" aria-labelledby="physical-exam-title">
            <h2 id="physical-exam-title" className="mb-1.5 text-sm font-semibold leading-[1.5]">
              {Locales.trip.medical_certificate.physical_exam}
            </h2>
            {[
              {
                id: 'psychomotor',
                label: Locales.trip.medical_certificate.psychomotor_disorders,
                desc: Locales.trip.medical_certificate.psychomotor_disorders_description,
                state: q7aIsIssuePsychomotor,
                setState: setQ7aIsIssuePsychomotor,
              },
              {
                id: 'locomotor',
                label: Locales.trip.medical_certificate.locomotor_disorders,
                desc: Locales.trip.medical_certificate.locomotor_disorders_description,
                state: q7bIsIssueLocomotor,
                setState: setQ7bIsIssueLocomotor,
              },
              {
                id: 'vision',
                label: Locales.trip.medical_certificate.vision_disorders,
                desc: Locales.trip.medical_certificate.vision_disorders_description,
                state: q7cIsIssueSeeing,
                setState: setQ7cIsIssueSeeing,
              },
              {
                id: 'chronic',
                label: Locales.trip.medical_certificate.chronic_disease,
                desc: Locales.trip.medical_certificate.chronic_disease_description,
                state: q7dIsIssueChronic,
                setState: setQ7dIsIssueChronic,
              },
            ].map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-5 border-b border-[#edf0ee] py-3 last:border-b-0 last:pb-0 max-sm:gap-3.5"
              >
                <Label
                  htmlFor={'medical-' + item.id}
                  className={
                    disabledForMSSecondary
                      ? 'min-w-0 flex-1 cursor-not-allowed opacity-60'
                      : 'min-w-0 flex-1 cursor-pointer'
                  }
                >
                  <span className="block text-[13px] font-medium leading-[1.5]">{item.label}</span>
                  <span className="mt-[3px] block text-xs font-normal leading-[1.6] text-[#66736d]">{item.desc}</span>
                </Label>
                <Switch
                  id={'medical-' + item.id}
                  checked={item.state}
                  onCheckedChange={item.setState}
                  disabled={disabledForMSSecondary}
                />
              </div>
            ))}
          </section>

          {isMSPrimary && isApprovedForMSStep !== null && previousLogs.length > 0 && (
            <section className="mb-6 mt-6 border-y border-[#e3e8e7]">
              <button
                type="button"
                className="flex w-full items-center justify-between gap-2.5 py-[17px] text-left text-sm font-semibold leading-[1.5] hover:text-hajj-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring max-sm:text-[13px]"
                aria-expanded={isHistoryOpened}
                onClick={() => setIsHistoryOpened(!isHistoryOpened)}
              >
                <span>
                  {Locales.trip.medical_certificate.history}{' '}
                  <span className="font-normal text-[#728076]">({previousLogs.length})</span>
                </span>
                <ChevronDown
                  className={
                    isHistoryOpened
                      ? 'size-4 shrink-0 rotate-180 transition-transform'
                      : 'size-4 shrink-0 transition-transform'
                  }
                />
              </button>
              {isHistoryOpened && <MedicalVisitHistory visits={previousLogs} layout="modal" />}
            </section>
          )}

          <div className={isMSPrimary && isApprovedForMSStep !== null && previousLogs.length > 0 ? '' : 'mt-6'}>
            <Label htmlFor="medical-notes" className="mb-2.5 block text-sm font-semibold">
              {Locales.trip.medical_certificate.notes}
            </Label>
            <Textarea
              id="medical-notes"
              placeholder={Locales.trip.medical_certificate.notes_description}
              value={healthNote}
              onChange={(event) => setHealthNote(event.target.value)}
              className="min-h-[102px] resize-y rounded-[7px] border-[#cdd7d0] p-3 leading-[1.6] placeholder:text-xs placeholder:text-[#88928d]"
            />
          </div>
        </div>

        <DialogFooter className="shrink-0 flex-row justify-end gap-[9px] border-t border-[#e3e8e7] bg-white px-6 py-3.5 max-sm:px-[18px] max-sm:py-3">
          <Button
            variant="outline"
            className="h-[39px] rounded-md border-[#cdd7d0] px-[17px] text-[13px] [font-weight:550]"
            onClick={closeModal}
          >
            {Locales.base.button.cancel}
          </Button>
          <Button
            variant="hajj"
            className="h-[39px] rounded-md px-[17px] text-[13px] shadow-none [font-weight:550] hover:shadow-none"
            onClick={handleMedicalCheckup}
            disabled={isMutating || disabledForMSSecondary}
          >
            {Locales.base.button.validate}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

MedicalCheckupModal.propTypes = {
  isOpened: PropTypes.bool,
  pilgrimData: PropTypes.shape({}),
  closeModal: PropTypes.func,
};

export default MedicalCheckupModal;
